import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner, faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import Button from '../Button';
import SearchSection from './SearchSection';
import ApiJobsList from './ApiJobsList';
import StaticJobsList from './StaticJobsList';
import { JobData } from '../../api/JobsService';

interface JobListContentProps {
  isLoadingJobs: boolean;
  isLoadingJobQuestionnaires?: boolean;
  jobsError: string | null;
  apiJobs: JobData[] | null;
  filteredJobs: JobData[];
  searchKeyword: string;
  selectedDivision: string;
  selectedUser: string;
  fetchJobs: () => void;
  handleSearch: (keyword: string, divisionId: string, userId: string) => void;
  handleStaticJobClick: (jobKey: 'marketingDirector' | 'devOpsEngineer') => void;
  handleJobClick: (jobId: number) => void;
  handleActionClick: (jobId?: number) => void;
  getActionButtonText: (jobId: number, isActive: boolean) => string;
  getActionButtonVariant: (jobId: number, isActive: boolean) => 'outline-primary' | 'outline-danger' | 'transparent';
  associatingJobs?: Set<number>;
}

const JobListContent: React.FC<JobListContentProps> = ({
  isLoadingJobs,
  isLoadingJobQuestionnaires = false,
  jobsError,
  apiJobs,
  filteredJobs,
  searchKeyword,
  selectedDivision,
  selectedUser,
  fetchJobs,
  handleSearch,
  handleStaticJobClick,
  handleJobClick,
  handleActionClick,
  getActionButtonText,
  getActionButtonVariant,
  associatingJobs
}) => {
  const isLoading = isLoadingJobs || isLoadingJobQuestionnaires;

  return (
    <div>
      <SearchSection onSearch={handleSearch} />
      
      {/* Loading state */}
      {isLoading && (
        <div className="text-center p-4">
          <FontAwesomeIcon icon={faSpinner} spin className="mb-2" style={{ fontSize: '24px' }} />
          <p>Loading jobs{isLoadingJobQuestionnaires ? ' and questionnaire data' : ''}...</p>
        </div>
      )}
      
      {/* Error state with fallback static jobs */}
      {!isLoading && jobsError && (
        <div>
          <div className="p-3 bg-danger-light text-danger border rounded mb-3 mt-3">
            <div className="d-flex align-items-center mb-2">
              <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
              <span className="fw-bold">Error Loading Jobs</span>
            </div>
            <p>{jobsError}</p>
            <div className="d-flex gap-2 mt-2">
              <Button 
                text="Try Again" 
                variant="outline-danger"
                onClick={fetchJobs}
              />
            </div>
          </div>
          
          {/* Show static job items as fallback when there's an error */}
          <div className="mt-4">
            <StaticJobsList
              handleStaticJobClick={handleStaticJobClick}
              handleActionClick={handleActionClick}
              getActionButtonText={getActionButtonText}
              getActionButtonVariant={getActionButtonVariant}
              message="Using demo data as fallback."
              associatingJobs={associatingJobs}
            />
          </div>
        </div>
      )}
      
      {/* No jobs state with fallback static jobs */}
      {!isLoading && !jobsError && apiJobs?.length === 0 && (
        <StaticJobsList
          handleStaticJobClick={handleStaticJobClick}
          handleActionClick={handleActionClick}
          getActionButtonText={getActionButtonText}
          getActionButtonVariant={getActionButtonVariant}
          message="No jobs found. Using demo data instead."
          associatingJobs={associatingJobs}
        />
      )}
      
      {/* Jobs list */}
      {!isLoading && !jobsError && filteredJobs.length > 0 && (
        <ApiJobsList
          jobs={filteredJobs}
          handleJobClick={handleJobClick}
          handleActionClick={handleActionClick}
          getActionButtonText={getActionButtonText}
          getActionButtonVariant={getActionButtonVariant}
          associatingJobs={associatingJobs}
        />
      )}
      
      {/* No jobs matching filters */}
      {!isLoading && !jobsError && filteredJobs.length === 0 && (apiJobs && apiJobs.length > 0) && (
        <div className="text-center p-4">
          <p>
            {(searchKeyword || selectedDivision !== 'all' || selectedUser !== 'all') ? 
              `No jobs found matching the selected filters.` :
              'No jobs found.'
            }
          </p>
          <Button 
            text="Clear Filters" 
            variant="outline-primary"
            onClick={() => {
              handleSearch('', 'all', 'all');
            }}
          />
        </div>
      )}
    </div>
  );
};

export default JobListContent; 